import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type { ModuloSistema } from '@suminuta/shared';

// =====================================================
// QUERY KEYS
// =====================================================

export const modulosKeys = {
  all: ['modulos-sistema'] as const,
  lists: () => [...modulosKeys.all, 'list'] as const,
  list: () => [...modulosKeys.lists()] as const,
  activos: () => [...modulosKeys.all, 'activos'] as const,
  details: () => [...modulosKeys.all, 'detail'] as const,
  detail: (id: number) => [...modulosKeys.details(), id] as const,
};

// =====================================================
// QUERIES
// =====================================================

/**
 * Hook para obtener todos los módulos del sistema
 */
export const useModulos = () => {
  return useQuery({
    queryKey: modulosKeys.list(),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: ModuloSistema[];
      }>('/roles-permisos/modulos');
      return response.data.data;
    },
  });
};

/**
 * Hook para obtener solo módulos activos
 */
export const useModulosActivos = () => {
  return useQuery({
    queryKey: modulosKeys.activos(),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: ModuloSistema[];
      }>('/roles-permisos/modulos/activos');
      return response.data.data;
    },
  });
};

/**
 * Hook para obtener un módulo por ID
 */
export const useModulo = (id: number) => {
  return useQuery({
    queryKey: modulosKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: ModuloSistema }>(
        `/roles-permisos/modulos/${id}`
      );
      return response.data.data;
    },
    enabled: !!id,
  });
};

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Hook para actualizar un módulo
 */
export const useUpdateModulo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<ModuloSistema>;
    }) => {
      const response = await api.patch<{ success: boolean; message: string }>(
        `/roles-permisos/modulos/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulosKeys.all });
    },
  });
};
